"use client";

import { useCallback, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { readDesktopBridge } from "@/lib/guide/desktop-shell";

// The desktop app's window during a call: `{ isSupported, appWindow, open,
// close }`. "Opening" shrinks the app's one window to the teleprompter's size
// and "closing" gives the panel the size it had back; the teleprompter is
// drawn inline, in this document.
//
// `appWindow` is a thin shim over this window. GuideTeleprompter reads its
// outer and inner size and calls resizeTo on it, and a frameless Electron
// window ignores the renderer's own window.resizeTo, so the shim routes that
// one call through the bridge and reads everything else live from `window`.

const noSubscription = () => () => {};
const onServer = () => null;

function windowShim(bridge) {
  return {
    document: window.document,
    get innerWidth() {
      return window.innerWidth;
    },
    get innerHeight() {
      return window.innerHeight;
    },
    get outerWidth() {
      return window.outerWidth;
    },
    get outerHeight() {
      return window.outerHeight;
    },
    resizeTo(width, height) {
      bridge.resizeTo(width, height);
    },
  };
}

/** The bridge, or null in a browser and on the server. */
export function useDesktopBridge() {
  return useSyncExternalStore(noSubscription, readDesktopBridge, onServer);
}

/**
 * @returns {{
 *   isSupported: boolean,
 *   appWindow: object|null,
 *   open: (options?: { width?: number, height?: number }) => Promise<object|null>,
 *   close: () => void,
 * }} `isSupported` is true only inside the desktop app. `open` resizes the
 * window to `width` × `height` and resolves with the shim; `close` gives the
 * window the size it had before `open` and clears `appWindow`.
 */
export function useDesktopShellWindow() {
  const bridge = useDesktopBridge();
  const shim = useMemo(() => (bridge ? windowShim(bridge) : null), [bridge]);
  const [appWindow, setAppWindow] = useState(null);
  const panelSize = useRef(null);

  const open = useCallback(
    ({ width, height } = {}) => {
      if (!shim) return Promise.resolve(null);
      if (!panelSize.current) {
        panelSize.current = { width: shim.outerWidth, height: shim.outerHeight };
      }
      if (width && height) shim.resizeTo(width, height);
      setAppWindow(shim);
      return Promise.resolve(shim);
    },
    [shim],
  );

  const close = useCallback(() => {
    const size = panelSize.current;
    panelSize.current = null;
    setAppWindow(null);
    if (size) shim?.resizeTo(size.width, size.height);
  }, [shim]);

  return { isSupported: Boolean(bridge), appWindow, open, close };
}
